import { forwardRef, useMemo, useRef, useState, type ComponentRef } from "react";

import { cn } from "../../lib";
import { composeRefs } from "../../primitives";
import {
  SelectContentPrimitive,
  SelectItemPrimitive,
  SelectRootPrimitive,
  SelectTriggerPrimitive,
  SelectValuePrimitive,
} from "../../primitives/select";
import { Icon } from "../icons";
import { textFieldPlaceholderColor } from "../text-field";
import { Typography } from "../typography";
import {
  getSelectVisualState,
  selectContentMaxHeight,
  selectIconSize,
  selectOptionIconSize,
} from "./lib";
import {
  selectTriggerTouchProps,
  selectTriggerValueProps,
  useSelectTriggerName,
} from "./select-trigger-props";
import {
  selectContentVariants,
  selectItemColor,
  selectItemVariants,
  selectTriggerIconVariants,
  selectTriggerVariants,
} from "./select.variants";
import type { SelectOption, SelectProps } from "./types";

export const Select = forwardRef<ComponentRef<typeof SelectTriggerPrimitive>, SelectProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder,
      state = "default",
      size = "sm",
      disabled = false,
      className,
      accessibilityLabel,
      testID,
      id,
    },
    ref,
  ) => {
    const valueObject = options.find((o) => o.value === value);
    const hasValue = Boolean(valueObject);
    const [isOpen, setIsOpen] = useState(false);
    const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
    const triggerRef = useRef<ComponentRef<typeof SelectTriggerPrimitive>>(null);
    const setRefs = useMemo(() => composeRefs(ref, triggerRef), [ref]);
    const triggerName = useSelectTriggerName(triggerRef, accessibilityLabel, placeholder);
    const handleValueChange = (newValue: string) => onValueChange?.(newValue);

    const visualState = getSelectVisualState({ disabled, isOpen, state });
    const iconClass = selectTriggerIconVariants({ disabled });
    return (
      <SelectRootPrimitive
        value={valueObject ?? null}
        onValueChange={handleValueChange}
        onOpenChange={setIsOpen}
        disabled={disabled}
      >
        <SelectTriggerPrimitive
          ref={setRefs}
          className={cn(selectTriggerVariants({ size, visualState }), className)}
          disabled={disabled}
          testID={testID}
          id={id}
          accessibilityLabel={triggerName}
          aria-haspopup="listbox"
          onTriggerLayout={setTriggerWidth}
          {...selectTriggerTouchProps(size)}
          {...selectTriggerValueProps(valueObject?.label)}
        >
          <Typography
            variant="body-md"
            color={
              disabled
                ? "neutral-foreground-attenuated"
                : hasValue
                  ? "neutral-foreground-default"
                  : textFieldPlaceholderColor[visualState === "open" ? "focused" : visualState]
            }
            className="line-clamp-1 flex-1 text-left"
          >
            <SelectValuePrimitive placeholder={placeholder} />
          </Typography>
          <Icon
            name="CaretDown"
            size={selectIconSize[size]}
            className={cn("shrink-0", iconClass)}
          />
        </SelectTriggerPrimitive>
        <SelectContentPrimitive
          className={selectContentVariants}
          scrollHeight={selectContentMaxHeight}
          style={triggerWidth ? { minWidth: triggerWidth } : undefined}
        >
          {options.map((option: SelectOption) => {
            const isSelected = option.value === value;
            const itemColor =
              selectItemColor[option.disabled ? "disabled" : isSelected ? "selected" : "default"];
            return (
              <SelectItemPrimitive
                key={option.value}
                value={option.value}
                label={option.label}
                disabled={option.disabled}
                className={selectItemVariants({
                  selected: isSelected,
                  disabled: Boolean(option.disabled),
                })}
              >
                {option.icon && (
                  <Icon
                    name={option.icon}
                    size={selectOptionIconSize}
                    color={itemColor}
                    className="shrink-0"
                  />
                )}
                <Typography
                  variant="body-sm"
                  numberOfLines={1}
                  selectable={false}
                  color={itemColor}
                  className="flex-1"
                >
                  {option.label}
                </Typography>
                {isSelected && (
                  <Icon
                    name="CheckCircle"
                    size={selectOptionIconSize}
                    weight="fill"
                    className="text-luce-brand-primary"
                  />
                )}
              </SelectItemPrimitive>
            );
          })}
        </SelectContentPrimitive>
      </SelectRootPrimitive>
    );
  },
);
Select.displayName = "Select";
